import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { 
  Assignment, 
  AutoAwesome, 
  Send,
  TrendingUp,
  AttachMoney,
  Group,
  Inventory,
  Campaign,
  Analytics,
  ArrowBack,
  Slideshow
} from '@mui/icons-material';
import HelmSpinner from '../components/HelmSpinner';
import ReactMarkdown from 'react-markdown';
import InlineChatChart from '../components/InlineChatChart';
import InlineDataTable from '../components/InlineDataTable';
import CollapsibleTable from '../components/CollapsibleTable';
import ClickableSKU from '../components/ClickableSKU';
import CreatePresentationModal from '../components/CreatePresentationModal';
import SessionListPage from './SessionListPage';
import { API_URL, fetchWithCredentials } from '../config';
import { saveSession, getSession, createSession } from '../utils/sessionStorage';

const SAMPLE_QUESTIONS = [
  {
    id: 'sku_reduction',
    question: 'Which SKUs should we reduce or remove for next season?'
  },
  {
    id: 'underperforming',
    question: 'Which products are underperforming and why?'
  },
  {
    id: 'inventory_optimization',
    question: 'How should we optimize inventory allocation across stores?'
  },
  {
    id: 'pricing_strategy',
    question: 'What pricing adjustments would improve margins?'
  },
  {
    id: 'channel_performance',
    question: 'Which sales channels are most profitable by product category?'
  },
  {
    id: 'seasonal_trends',
    question: 'What seasonal patterns should inform our planning?'
  }
];

const PlanPage = () => {
  // Session management
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'chat' - start with list
  const [currentSessionId, setCurrentSessionId] = useState(null);
  
  const [selectedProblem, setSelectedProblem] = useState(null);
  const [chatMessages, setChatMessages] = useState([
    {
      id: 1,
      message: "What problem are you thinking about?",
      isUser: false,
      timestamp: new Date().toLocaleTimeString(),
      isLarge: true
    },
    {
      id: 2,
      message: "I can help you analyze your product performance, optimize SKU mix, improve margins, and make data-driven decisions. Choose a common question below or describe your specific challenge.",
      isUser: false,
      timestamp: new Date().toLocaleTimeString()
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [currentSuggestions, setCurrentSuggestions] = useState([
    'Which SKUs should we reduce or remove for next season?',
    'Which products are underperforming and why?', 
    'How should we optimize inventory allocation across stores?',
    'What pricing adjustments would improve margins?'
  ]);
  const inputRef = useRef(null);
  const messagesEndRef = useRef(null);
  const [currentSteps, setCurrentSteps] = useState([]);
  const [currentStep, setCurrentStep] = useState('');
  const [presentationModalOpen, setPresentationModalOpen] = useState(false);
  const [firstUserQuestion, setFirstUserQuestion] = useState(null);

  // Auto-save helper function
  const autoSaveSession = useCallback(() => {
    if (!currentSessionId) return;
    
    const firstUserMsg = chatMessages.find(m => m.isUser);
    if (!firstUserMsg) return; // Don't save if no user messages yet
    
    const title = firstUserMsg.message.substring(0, 60);
    const session = {
      id: currentSessionId,
      title,
      messages: chatMessages,
      messageCount: chatMessages.length,
      corpusId: localStorage.getItem('corpus_id') || 'demo-skipper-corpus-2024'
    };
    
    saveSession(session);
    console.log('🔄 Auto-saved session:', session.id.substring(0, 8));
  }, [currentSessionId, chatMessages]);

  // Auto-scroll ONLY when new user messages are sent, not during analysis
  // This lets users read the analysis steps without being pulled to bottom
  useEffect(() => {
    // Only scroll if not currently loading
    if (!isLoading) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatMessages.length, isLoading]);  // Only on new messages, not during updates

  // Session handlers
  const handleNewSession = () => {
    // Generate a NEW session ID immediately for the fresh session
    const newSessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    // Completely reset ALL state for fresh session
    setChatMessages([
      {
        id: Date.now(),
        message: "What problem are you thinking about?",
        isUser: false,
        timestamp: new Date().toLocaleTimeString(),
        isLarge: true
      },
      {
        id: Date.now() + 1,
        message: "I can help you analyze your product performance, optimize SKU mix, improve margins, and make data-driven decisions. Choose a common question below or describe your specific challenge.",
        isUser: false,
        timestamp: new Date().toLocaleTimeString()
      }
    ]);
    setCurrentSuggestions([
      'Which SKUs should we reduce or remove for next season?',
      'Which products are underperforming and why?', 
      'How should we optimize inventory allocation across stores?',
      'What pricing adjustments would improve margins?'
    ]);
    setInputValue('');
    setIsLoading(false);
    setCurrentSteps([]);
    setCurrentStep('');
    setSelectedProblem(null);
    setFirstUserQuestion(null);
    setCurrentSessionId(newSessionId); // Set NEW session ID immediately
    setViewMode('chat');
  };

  const handleSelectSession = (session) => {
    // Set the corpus for this session if it's different
    const sessionCorpus = session.corpusId || 'demo-skipper-corpus-2024';
    const currentCorpus = localStorage.getItem('corpus_id');
    
    if (sessionCorpus !== currentCorpus) {
      localStorage.setItem('corpus_id', sessionCorpus);
      console.log(`📂 Switched to corpus: ${sessionCorpus}`);
    }
    
    setChatMessages(session.messages || []);
    setCurrentSessionId(session.id);
    setViewMode('chat');
  };

  const handleSaveCurrentSession = () => {
    // Auto-save with first user message as title
    const firstUserMsg = chatMessages.find(m => m.isUser);
    const title = firstUserMsg ? firstUserMsg.message.substring(0, 60) : 'Analysis Session';
    
    // Always use the current session ID (should never be null at this point)
    if (!currentSessionId) {
      console.error('No session ID found - this should not happen!');
      return;
    }
    
    const session = {
      id: currentSessionId,
      title,
      messages: chatMessages,
      messageCount: chatMessages.length,
      corpusId: localStorage.getItem('corpus_id') || 'demo-skipper-corpus-2024'
    };
    
    saveSession(session);
    
    // Navigate back to session list
    setViewMode('list');
  };

  const handleSuggestionClick = (suggestion, messageId = null) => {
    // Hide suggestions immediately when clicked
    setCurrentSuggestions([]);
    
    // If this is a clarification message, mark the selected option
    if (messageId) {
      setChatMessages(prev => prev.map(msg => 
        msg.id === messageId 
          ? { ...msg, selectedOption: suggestion, answered: true }
          : msg
      ));
    }
    
    // Post user message immediately
    const userMessage = {
      id: Date.now(),
      message: suggestion,
      isUser: true,
      timestamp: new Date().toLocaleTimeString()
    };
    
    setChatMessages(prev => [...prev, userMessage]);
    setIsLoading(true);
    
    // Send to backend
    setTimeout(async () => {
      try {
        const response = await fetchWithCredentials(`${API_URL}/api/chat/stream`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            message: suggestion,
            history: chatMessages
          })
        });
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop();
          
          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const data = JSON.parse(line.slice(6));
                handleStreamingData(data);
              } catch (e) {
                console.error('Error parsing:', e);
              }
            }
          }
        }
      } catch (error) {
        console.error('Error:', error);
        setIsLoading(false);
      }
    }, 10);
  };

  const handleStreamingData = (data) => {
    console.log('Streaming data received:', data.type, data.message || data.step?.description);
    
    switch (data.type) {
      case 'start':
      case 'status':
        setCurrentStep(data.message);
        setChatMessages(prev => {
          // Collapse all previous interim messages
          const updated = prev.map(msg => msg.isInterim ? { ...msg, collapsed: true } : msg);
          return [...updated, {
            id: Date.now() + Math.random(),
            message: data.message,
            isUser: false,
            timestamp: new Date().toLocaleTimeString(),
            isInterim: true,
            collapsed: false,
            icon: '🔄'
          }];
        });
        break;
        
      case 'step_planning':
        setCurrentStep(`AI deciding step ${data.step}...`);
        setChatMessages(prev => {
          const updated = prev.map(msg => msg.isInterim ? { ...msg, collapsed: true } : msg);
          return [...updated, {
            id: Date.now() + Math.random(),
            message: `Step ${data.step}: AI deciding step...`,
            isUser: false,
            timestamp: new Date().toLocaleTimeString(),
            isInterim: true,
            collapsed: false,
            icon: '🤔'
          }];
        });
        break;
        
      case 'step_deciding':
      case 'step_executing':
        setCurrentStep(data.description || `Executing...`);
        setChatMessages(prev => {
          const updated = prev.map(msg => msg.isInterim ? { ...msg, collapsed: true } : msg);
          return [...updated, {
            id: Date.now() + Math.random(),
            message: `Step ${data.step}: ${data.description || 'Executing...'}`,
            isUser: false,
            timestamp: new Date().toLocaleTimeString(),
            isInterim: true,
            collapsed: false,
            icon: '⚡'
          }];
        });
        break;
        
      case 'step_analyzing':
        setCurrentStep('AI analyzing results...');
        setChatMessages(prev => {
          const updated = prev.map(msg => msg.isInterim ? { ...msg, collapsed: true } : msg);
          return [...updated, {
            id: Date.now() + Math.random(),
            message: `Step ${data.step}: AI analyzing results...`,
            isUser: false,
            timestamp: new Date().toLocaleTimeString(),
            isInterim: true,
            collapsed: false,
            icon: '🔍'
          }];
        });
        break;
        
      case 'step_complete':
        const step = data.step;
        
        let resultMessage = `✅ **Step ${step.step} Complete:** ${step.description}\n\n`;
        
        if (step.insight) {
          resultMessage += step.insight;
        }
        
        // Add data summary
        if (step.result.success && step.result.count !== undefined) {
          resultMessage += `\n\n📊 **Found ${step.result.count} records**`;
        }
        
        // Remove interim messages and add step result
        setChatMessages(prev => {
          const withoutInterim = prev.filter(msg => !msg.isInterim);
          return [...withoutInterim, {
            id: Date.now() + Math.random(),
            message: resultMessage,
            isUser: false,
            timestamp: new Date().toLocaleTimeString(),
            chartData: step.result.chart_data,
            chartConfig: step.result.chart_config,
            tableData: step.result.table_data
          }];
        });
        break;
        
      case 'analysis_complete':
        setIsLoading(false);
        setCurrentStep('');
        break;
        
      case 'clarification':
        // Remove interim messages
        setChatMessages(prev => prev.filter(msg => !msg.isInterim));
        
        setChatMessages(prev => [...prev, {
          id: Date.now() + Math.random(),
          message: data.message,
          isUser: false,
          timestamp: new Date().toLocaleTimeString(),
          isClarification: true,
          options: data.options  // Clickable options embedded in message
        }]);
        setIsLoading(false);
        
        // Don't use suggestions bar - options are inline in the message
        
        // Keep input focused for user response
        setTimeout(() => {
          if (inputRef.current) {
            inputRef.current.focus();
          }
        }, 100);
        break;
        
      case 'conversation_check':
        setChatMessages(prev => [...prev, {
          id: Date.now() + Math.random(),
          message: data.message,
          isUser: false,
          timestamp: new Date().toLocaleTimeString(),
          isConversationCheck: true
        }]);
        setIsLoading(false);
        
        // Add quick response buttons
        setCurrentSuggestions([
          'A) Continue the previous analysis',
          'B) Start fresh with this new question',
          'Show me what analysis was in progress',
          'Combine both questions in the analysis'
        ]);
        
        // Keep input focused for user response
        setTimeout(() => {
          if (inputRef.current) {
            inputRef.current.focus();
          }
        }, 100);
        break;
        
      case 'retry':
        setCurrentStep(data.message);
        break;
        
      case 'final':
        // Remove all interim messages and add final conclusion
        setChatMessages(prev => {
          const withoutInterim = prev.filter(msg => !msg.isInterim);
          const finalMessage = {
            id: Date.now() + Math.random(),
            message: `## 📝 Conclusion\n\n${data.message}`,
            isUser: false,
            timestamp: new Date().toLocaleTimeString(),
            isFinal: true,
            tableData: data.summary_table,
            chartData: data.summary_chart_data,
            chartConfig: data.summary_chart_config
          };
          
          const updatedMessages = [...withoutInterim, finalMessage];
          
          // Auto-save with updated messages (after state is set)
          setTimeout(() => {
            if (currentSessionId) {
              const firstUserMsg = updatedMessages.find(m => m.isUser);
              if (firstUserMsg) {
                const session = {
                  id: currentSessionId,
                  title: firstUserMsg.message.substring(0, 60),
                  messages: updatedMessages,
                  messageCount: updatedMessages.length,
                  corpusId: localStorage.getItem('corpus_id') || 'demo-skipper-corpus-2024'
                };
                saveSession(session);
                console.log('🔄 Auto-saved session with final message');
              }
            }
          }, 100);
          
          return updatedMessages;
        });
        
        // Update suggestions
        setIsLoading(false);
        
        setTimeout(() => {
          if (data.suggestions && data.suggestions.length > 0) {
            setCurrentSuggestions(data.suggestions);
          }
        }, 500);
        
        // Keep input focused
        setTimeout(() => {
          if (inputRef.current) {
            inputRef.current.focus();
          }
        }, 100);
        break;
        
      case 'error':
        setChatMessages(prev => [...prev, {
          id: Date.now() + Math.random(),
          message: `❌ **Error:** ${data.message}`,
          isUser: false,
          timestamp: new Date().toLocaleTimeString(),
          isError: true
        }]);
        setIsLoading(false);
        // Auto-save even on error (to preserve conversation)
        setTimeout(() => autoSaveSession(), 500);
        break;
    }
  };

  const handleQuestionSelect = (question) => {
    setSelectedProblem(question);
    
    // Add user message
    const userMessage = {
      id: Date.now(),
      message: question.question,
      isUser: true,
      timestamp: new Date().toLocaleTimeString()
    };
    
    // Add AI response based on question type
    let aiResponse = '';
    switch (question.id) {
      case 'sku_reduction':
        aiResponse = `Let me analyze your SKU performance data.\n\nBased on your sell-through data, I can identify SKUs with:\n• Low sales velocity\n• Poor margin performance\n• High inventory levels\n• Declining trends\n\nWould you like me to run this analysis and show you the specific SKUs to consider reducing?`;
        break;
      case 'underperforming':
        aiResponse = `I'll analyze underperforming products across multiple dimensions:\n\n• Sales volume vs targets\n• Margin performance\n• Inventory turnover\n• Channel performance\n• Seasonal patterns\n\nShall I pull the data and identify your underperformers with specific reasons?`;
        break;
      case 'inventory_optimization':
        aiResponse = `For inventory optimization, I can analyze:\n\n• Store-level sell-through rates\n• Regional demand patterns\n• Seasonal inventory needs\n• Stock-out risks vs overstock costs\n\nLet me examine your current allocation - would you like store-level or regional analysis first?`;
        break;
      default:
        aiResponse = `Great question! Let me analyze your data to provide specific insights about this. I'll look at your products and sales data to give you actionable recommendations.\n\nWhat specific aspect would you like me to focus on first?`;
    }
    
    const aiMessage = {
      id: Date.now() + 1,
      message: aiResponse,
      isUser: false,
      timestamp: new Date().toLocaleTimeString()
    };
    
    setChatMessages(prev => [...prev, userMessage, aiMessage]);
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;
    
    const userMessage = {
      id: Date.now(),
      message: inputValue,
      isUser: true,
      timestamp: new Date().toLocaleTimeString()
    };
    
    setChatMessages(prev => [...prev, userMessage]);
    setIsLoading(true);
    
    try {
      // Call Flask backend (simple endpoint for now to test)
      const response = await fetchWithCredentials(`${API_URL}/api/chat/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          message: inputValue,
          history: chatMessages
        })
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      // Handle streaming response properly
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop(); // Keep incomplete line in buffer
        
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              handleStreamingData(data);
            } catch (e) {
              console.error('Error parsing streaming data:', e);
            }
          }
        }
      }
      
    } catch (error) {
      console.error('Chat error:', error);
      
      const errorResponse = {
        id: Date.now() + 1,
        message: `I apologize, but I encountered an error processing your request. Please ensure the backend is running and try again.\n\n**Error:** ${error.message}`,
        isUser: false,
        timestamp: new Date().toLocaleTimeString()
      };
      
      setChatMessages(prev => [...prev, errorResponse]);
      setIsLoading(false);
      
      // Keep input focused even after errors
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 100);
    } finally {
      setInputValue('');
    }
  };


  // Function to convert SKU codes in text to clickable links
  const renderMessageWithClickableSKUs = (text) => {
    if (typeof text !== 'string') {
      // If it's a React element or array, recursively process children
      if (React.isValidElement(text)) {
        return text;
      }
      if (Array.isArray(text)) {
        return text.map((item, i) => (
          <React.Fragment key={i}>
            {renderMessageWithClickableSKUs(item)}
          </React.Fragment>
        ));
      }
      return text;
    }
    
    // Regex to match SKU patterns: SKP-##### -XXX (5 digits, 3 letters)
    const skuRegex = /SKP-\d{5}-[A-Z]{3}/g;
    
    const parts = [];
    let lastIndex = 0;
    let match;
    
    // Reset regex
    skuRegex.lastIndex = 0;
    
    while ((match = skuRegex.exec(text)) !== null) {
      // Add text before match
      if (match.index > lastIndex) {
        parts.push(text.substring(lastIndex, match.index));
      }
      // Add SKU as clickable component
      parts.push(
        <ClickableSKU 
          key={`sku-${match.index}-${match[0]}`}
          sku={match[0]} 
          inline={true}
          showIcon={false}
        />
      );
      lastIndex = match.index + match[0].length;
    }
    
    // Add remaining text
    if (lastIndex < text.length) {
      parts.push(text.substring(lastIndex));
    }
    
    return parts.length > 0 ? parts : text;
  };

  // Memoized Message component to prevent unnecessary re-renders
  const Message = React.memo(({ message, isUser, timestamp, isLarge, chartData, chartConfig, tableData, isInterim, collapsed, icon, isClarification, options, selectedOption, answered, id }) => {
    // Clarification messages with clickable options - styled like suggestions
    if (isClarification) {
      return (
        <Box sx={{ mb: 2 }}>
          <Paper
            elevation={1}
            sx={{
              p: 2,
              backgroundColor: 'rgba(71, 189, 198, 0.05)',
              border: '1px solid',
              borderColor: 'primary.main',
              borderRadius: 2
            }}
          >
            <Typography variant="body2" sx={{ mb: 2, fontWeight: 600, color: 'primary.dark', fontSize: '0.9rem' }}>
              {message}
            </Typography>
            
            {options && options.length > 0 && (
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                {options.map((option, idx) => {
                  const isSelected = answered && selectedOption === option;
                  const isOtherOption = answered && selectedOption !== option;
                  
                  return (
                    <Button
                      key={idx}
                      variant={isSelected ? 'contained' : 'outlined'}
                      size="small"
                      onClick={() => answered ? null : handleSuggestionClick(option, id)}
                      disabled={answered}
                      sx={{
                        flex: options.length <= 4 ? '1 1 0' : '0 0 auto',
                        textTransform: 'none',
                        fontSize: '0.75rem',
                        py: 0.75,
                        px: 1.5,
                        lineHeight: 1.3,
                        whiteSpace: 'normal',
                        textAlign: 'left',
                        borderColor: isSelected ? '#9e9e9e' : (isOtherOption ? 'divider' : 'primary.main'),
                        color: isSelected ? 'white' : (isOtherOption ? 'text.disabled' : 'primary.dark'),
                        backgroundColor: isSelected ? '#757575' : (isOtherOption ? 'transparent' : 'rgba(71, 189, 198, 0.05)'),
                        cursor: answered ? 'default' : 'pointer',
                        boxShadow: 'none',
                        '&:hover': answered ? {} : {
                          backgroundColor: 'rgba(71, 189, 198, 0.15)',
                          borderColor: 'primary.dark',
                          boxShadow: 'none'
                        },
                        '&.Mui-disabled': {
                          borderColor: isSelected ? '#9e9e9e' : 'divider',
                          color: isSelected ? 'white' : 'text.disabled',
                          backgroundColor: isSelected ? '#757575' : 'transparent',
                          boxShadow: 'none'
                        }
                      }}
                    >
                      {option}
                    </Button>
                  );
                })}
              </Box>
            )}
            
            {!answered && (
              <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary', fontSize: '0.75rem', fontStyle: 'italic' }}>
                💡 Click an option above or type your own answer below
              </Typography>
            )}
          </Paper>
        </Box>
      );
    }
    
    // Interim messages (thinking/planning/executing) - collapse when not latest
    if (isInterim) {
      if (collapsed) {
        return (
          <Box sx={{ mb: 0.25 }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem', opacity: 0.6 }}>
              {icon} {message}
            </Typography>
          </Box>
        );
      } else {
        // Latest interim message - show with spinner
        return (
          <Box sx={{ mb: 0.75 }}>
            <Paper
              elevation={0}
              sx={{
                px: 1.5,
                py: 0.75,
                backgroundColor: 'rgba(71, 189, 198, 0.08)',
                borderLeft: 2,
                borderColor: 'primary.main',
                display: 'inline-block'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <HelmSpinner size={14} showText={false} />
                <Typography variant="caption" sx={{ color: 'primary.dark', fontWeight: 600, fontSize: '0.75rem' }}>
                  {icon} {message}
                </Typography>
              </Box>
            </Paper>
          </Box>
        );
      }
    }
    
    // Regular messages
    const isFinalMessage = message.includes('📝 Conclusion') || message.includes('## Conclusion');
    
    return (
    <Box sx={{ mb: 1.5, display: 'flex', justifyContent: isUser ? 'flex-end' : 'flex-start' }}>
      <Paper
        elevation={1}
        sx={{
          p: isFinalMessage ? 3 : (isLarge ? 2 : 1),  // Final gets more padding
          maxWidth: isFinalMessage ? '100%' : (isLarge ? '100%' : '95%'),  // Wider messages
          backgroundColor: isUser ? 'primary.main' : 'white',
          color: isUser ? 'white' : 'text.primary',
          borderRadius: 2,
          borderBottomRightRadius: isUser ? 0 : 2,
          borderBottomLeftRadius: isUser ? 2 : 0,
          border: isUser ? 'none' : '1px solid',
          borderColor: 'divider'
        }}
      >
        {/* Always use 2-column layout if chart exists */}
        {chartData && chartConfig && !isUser ? (
          <>
            <Grid container spacing={1}>
              {/* Text column (left 65%) */}
              <Grid item xs={12} sx={{ width: { xs: '100%', md: '65%' } }}>
                <ReactMarkdown
                  components={{
                    p: ({ children }) => (
                      <Typography variant="body2" component="p" sx={{ mb: isFinalMessage ? 1.5 : 0.5, lineHeight: isFinalMessage ? 1.7 : 1.4, fontSize: isFinalMessage ? '0.95rem' : '0.85rem' }}>
                        {renderMessageWithClickableSKUs(children)}
                      </Typography>
                    ),
                    text: ({ children }) => renderMessageWithClickableSKUs(children),
                    strong: ({ children }) => (
                      <Box component="span" sx={{ fontWeight: 700, color: 'primary.main', backgroundColor: 'rgba(71, 189, 198, 0.1)', px: 0.5, py: 0.25, borderRadius: 1 }}>
                        {renderMessageWithClickableSKUs(children)}
                      </Box>
                    ),
                    ul: ({ children }) => (
                      <Box component="ul" sx={{ pl: 2, my: 0.5 }}>{children}</Box>
                    ),
                    li: ({ children }) => (
                      <Typography component="li" variant="body2" sx={{ mb: 0.25, fontSize: '0.85rem' }}>{children}</Typography>
                    )
                  }}
                >
                  {message}
                </ReactMarkdown>
              </Grid>
              
              {/* Chart column (right 35%) - FULL HEIGHT */}
              <Grid item xs={12} sx={{ width: { xs: '100%', md: 'calc(35% - 16px)' } }}>
                <Box sx={{ height: '100%', minHeight: 300 }}>
                <InlineChatChart 
                  data={chartData}
                  chartType={chartConfig.type}
                  title={chartConfig.title}
                  description={chartConfig.description}
                  chartConfig={chartConfig}
                />
                </Box>
              </Grid>
            </Grid>
            
            {/* Table below if it exists - collapsible by default */}
            {tableData && tableData.columns && tableData.rows && (
              <CollapsibleTable 
                columns={tableData.columns}
                rows={tableData.rows}
                title="Detailed Results"
                defaultExpanded={isFinalMessage}
                sparklines={tableData.sparklines}
              />
            )}
          </>
        ) : (
          /* No chart - regular layout */
          <>
            {isUser ? (
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: 'white' }}>
                {message}
              </Typography>
            ) : (
              <Box>
                <ReactMarkdown
                  components={{
                    p: ({ children }) => (
                      <Typography variant="body2" component="p" sx={{ mb: isFinalMessage ? 1.5 : 0.5, lineHeight: isFinalMessage ? 1.7 : 1.4, fontSize: isFinalMessage ? '0.95rem' : '0.85rem' }}>
                        {renderMessageWithClickableSKUs(children)}
                      </Typography>
                    ),
                    text: ({ children }) => renderMessageWithClickableSKUs(children),
                  strong: ({ children }) => (
                    <Box component="span" sx={{ fontWeight: 700, color: 'primary.main', backgroundColor: 'rgba(71, 189, 198, 0.1)', px: 0.5, py: 0.25, borderRadius: 1 }}>
                      {renderMessageWithClickableSKUs(children)}
                    </Box>
                  ),
                  ul: ({ children }) => (
                    <Box component="ul" sx={{ pl: 2, my: 1 }}>{children}</Box>
                  ),
                  li: ({ children }) => (
                    <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                      {renderMessageWithClickableSKUs(children)}
                    </Typography>
                  )
                  }}
                >
                  {message}
                </ReactMarkdown>
                
                {/* Table below text if no chart - collapsible */}
                {tableData && tableData.columns && tableData.rows && (
                  <CollapsibleTable 
                    columns={tableData.columns}
                    rows={tableData.rows}
                    title="Detailed Results"
                    defaultExpanded={isFinalMessage}
                    sparklines={tableData.sparklines}
                  />
                )}
              </Box>
            )}
          </>
        )}
        
        {timestamp && (
          <Typography variant="caption" sx={{ opacity: 0.7, display: 'block', mt: 0.5 }}>
            {timestamp}
          </Typography>
        )}
      </Paper>
    </Box>
    );
  }, (prevProps, nextProps) => {
    // Custom comparison: only re-render if props actually changed
    return (
      prevProps.message === nextProps.message &&
      prevProps.isUser === nextProps.isUser &&
      prevProps.chartData === nextProps.chartData &&
      prevProps.chartConfig === nextProps.chartConfig &&
      prevProps.tableData === nextProps.tableData &&
      prevProps.isInterim === nextProps.isInterim &&
      prevProps.collapsed === nextProps.collapsed &&
      prevProps.icon === nextProps.icon &&
      prevProps.isClarification === nextProps.isClarification &&
      prevProps.options === nextProps.options &&
      prevProps.selectedOption === nextProps.selectedOption &&
      prevProps.answered === nextProps.answered
    );
  });

  // Show session list or chat based on view mode
  if (viewMode === 'list') {
    return <SessionListPage onSelectSession={handleSelectSession} onNewSession={handleNewSession} />;
  }

  return (
    <Box sx={{ height: '100%' }}>
      {/* AI Assistant - Full viewport */}
      <Box 
        sx={{ 
          height: '100%',
          display: 'flex', 
          flexDirection: 'column',
          backgroundColor: 'white',
          borderRadius: 2,
          boxShadow: 1,
          border: '1px solid',
          borderColor: 'divider'
        }}
      >
        {/* Minimal Header */}
        <Box sx={{ 
          px: 2,
          py: 1.5,
          borderBottom: 1, 
          borderColor: 'divider',
          backgroundColor: 'primary.main',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton 
              size="small" 
              onClick={() => setViewMode('list')}
              sx={{ color: 'white' }}
            >
              <ArrowBack />
            </IconButton>
            <Typography variant="subtitle1" sx={{ 
              fontWeight: 600,
              color: 'white',
              fontSize: '0.95rem'
            }}>
              <AutoAwesome sx={{ fontSize: 20, mr: 0.5 }} />
              AI Assistant
            </Typography>
          </Box>
          {chatMessages.some(msg => msg.isUser) && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="text"
                size="small"
                startIcon={<Slideshow />}
                onClick={() => setPresentationModalOpen(true)}
                sx={{ color: 'white', fontSize: '0.75rem' }}
              >
                Create Presentation
              </Button>
              <Button
                variant="text"
                size="small"
                onClick={handleSaveCurrentSession}
                sx={{ color: 'white', fontSize: '0.75rem' }}
              >
                Save & Exit
              </Button>
            </Box>
          )}
        </Box>

        {/* Messages Area - Maximized */}
        <Box sx={{ flexGrow: 1, overflow: 'auto', p: 2 }}>
          {chatMessages.map((msg) => (
            <Message 
              key={msg.id}
              id={msg.id}
              message={msg.message} 
              isUser={msg.isUser} 
              timestamp={msg.timestamp}
              isLarge={msg.isLarge}
              chartData={msg.chartData}
              chartConfig={msg.chartConfig}
              tableData={msg.tableData}
              isInterim={msg.isInterim}
              collapsed={msg.collapsed}
              icon={msg.icon}
              isClarification={msg.isClarification}
              options={msg.options}
              selectedOption={msg.selectedOption}
              answered={msg.answered}
            />
          ))}
          
          {/* Spacer for comfortable scrolling - 40% of viewport height */}
          <Box sx={{ height: '40vh' }} />
          
          <div ref={messagesEndRef} />
        </Box>

        {/* Compact Suggestions - Hidden when loading */}
        {!isLoading && currentSuggestions.length > 0 && (
          <Box sx={{ px: 2, pb: 1.5, borderTop: 1, borderColor: 'divider', backgroundColor: 'rgba(71, 189, 198, 0.05)' }}>
            <Typography variant="caption" sx={{ mb: 1, fontWeight: 600, display: 'block', color: 'primary.main' }}>
              💡 Next Steps:
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {currentSuggestions.slice(0, 3).map((suggestion, index) => (
                <Button
                  key={`suggestion-${index}`}
                  variant="outlined"
                  size="small"
                  onClick={() => handleSuggestionClick(suggestion)}
                  sx={{ 
                    flex: '1 1 0',
                    fontSize: '0.7rem',
                    textTransform: 'none',
                    px: 1.5,
                    py: 0.75,
                    lineHeight: 1.3,
                    whiteSpace: 'normal',
                    textAlign: 'left',
                    justifyContent: 'flex-start',
                    minHeight: 0,
                    borderColor: 'primary.main',
                    color: 'primary.dark',
                    '&:hover': {
                      backgroundColor: 'rgba(71, 189, 198, 0.1)',
                      borderColor: 'primary.dark'
                    }
                  }}
                >
                  {suggestion}
                </Button>
              ))}
            </Box>
          </Box>
        )}

        {/* Input Area - Compact */}
        <Box sx={{ p: 1.5, borderTop: 1, borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              placeholder="Ask a question..."
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && !isLoading && handleSendMessage()}
              disabled={isLoading}
              inputRef={inputRef}
            />
            <IconButton 
              color="primary" 
              onClick={handleSendMessage}
              disabled={!inputValue.trim() || isLoading}
              size="small"
              sx={{ 
                backgroundColor: 'primary.main',
                color: 'white',
                '&:hover': {
                  backgroundColor: 'primary.dark'
                },
                '&:disabled': {
                  backgroundColor: 'grey.300',
                  color: 'grey.500'
                }
              }}
            >
              <Send fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      </Box>

      {/* Create Presentation Modal */}
      <CreatePresentationModal
        open={presentationModalOpen}
        onClose={() => setPresentationModalOpen(false)}
        chatMessages={chatMessages}
        chatQuestion={selectedProblem || 'Analysis Question'}
        onPresentationCreated={(presentation) => {
          // Presentation already saved to Firestore by backend
          console.log('✅ Presentation created and saved to Firestore:', presentation);
        }}
      />
    </Box>
  );
};

export default PlanPage;
